import styled from 'styled-components';
import { JoinInput, JoinPhoneInput } from '../../../components/items/joinInput';
import { FileUploadImg } from '../../../components/items/uploadImg';
import { InputBtn } from '../../../components/items/inputBtn';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  checkPassid,
  driverSignup,
  requestPhoneAuth,
  uploadVehicleRegistration,
} from '../../../api/join';
import { AlertModal } from '../../../components/modal/alert.modal';
import { useState } from 'react';
import { joinSchema } from '../../../schema/joinSchema';
import { carNoValPartial, formatPhoneNumber } from '../../../utils/util';
import { useHistory } from 'react-router-dom';

// 데이터 타입 정의
interface JoinFormValues {
  passid: string;
  password: string;
  passwordConfirm: string;
  phone: string;
  registrationFile: File | null;
  authCode?: string; // 인증번호 필드 추가
}

const Join = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [phoneAuthUUID, setPhoneAuthUUID] = useState<string | null>(null); // 인증 UUID 상태 추가
  const [isPhoneVerified, setIsPhoneVerified] = useState(false); // 전화번호 인증 여부 상태 추가
  const [isPhoneDisabled, setIsPhoneDisabled] = useState(false);
  const [isSignupComplete, setIsSignupComplete] = useState(false); // 회원 가입 완료 여부 상태
  const [isIdChecked, setIsIdChecked] = useState(false); // 중복 확인 여부 상태 추가
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null); // 타이머 상태 추가
  const [remainingTime, setRemainingTime] = useState(180); // 남은 시간(초) 상태 추가

  const history = useHistory();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<JoinFormValues>({
    resolver: zodResolver(joinSchema),
  });

  const onSubmit = async (data: JoinFormValues) => {
    if (!isIdChecked) {
      setModalMessage('차량 번호 중복 확인을 완료해주세요.');
      setIsModalOpen(true);
      return;
    }

    if (!isPhoneVerified) {
      setModalMessage('휴대폰 번호 인증을 완료해주세요.');
      setIsModalOpen(true);
      return;
    }

    try {
      let registrationUrl = '';

      if (data.registrationFile) {
        // 파일 업로드 후 URL을 받아온다.
        registrationUrl = await uploadVehicleRegistration(
          data.registrationFile,
        );
      }

      // 회원가입 API 호출 시, 파일 URL을 전달한다.
      const response = await driverSignup({
        passid: data.passid,
        password: data.password,
        phone: data.phone,
        registration: registrationUrl, // 여기서는 반드시 URL이 전달되어야 함
      });

      console.log('가입 완료', response);
      history.push('/joinComplate');
    } catch (error) {
      console.error('회원가입 실패:', error);
    }
  };

  const idCheckHandler = async () => {
    try {
      const passid = getValues('passid'); // 현재 입력된 차량 번호 값 가져오기
      if (passid) {
        // 1. 차량 번호 형식 검증 (carNoValPartial)
        const isValidCarNo = carNoValPartial(passid);
        if (!isValidCarNo) {
          setModalMessage('차량 번호 형식이 올바르지 않습니다.');
          setIsModalOpen(true); // 차량 번호 형식 오류 모달 열기
          return; // 차량 번호 형식이 잘못된 경우 중복 확인을 진행하지 않음
        }

        // 2. 차량 번호 중복 확인 API 호출
        const message = await checkPassid(passid);
        setModalMessage(message);
        setIsModalOpen(true); // 중복 확인 결과 모달 열기
        setIsIdChecked(true); // 중복 확인이 성공적으로 완료되었음을 표시
      } else {
        setModalMessage('차량번호를 입력해주세요.');
        setIsModalOpen(true); // 중복 확인 결과 모달 열기
      }
    } catch (error: any) {
      console.log(error.response);
      if (error.response && error.response.status === 409) {
        setModalMessage(error.response.data.message);
      } else {
        console.error('아이디 중복 확인 실패:', error);
        setModalMessage('아이디 중복 확인에 실패하였습니다.');
      }
      setIsModalOpen(true);
    }
  };

  const startTimer = () => {
    if (timer) {
      clearTimeout(timer);
    }

    setRemainingTime(180); // 타이머 시작 시 3분 설정

    const newTimer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(newTimer);
          setPhoneAuthUUID(null);
          setModalMessage('인증 시간이 만료되었습니다. 다시 시도해주세요.');
          setIsModalOpen(true);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000); // 1초마다 타이머 업데이트

    setTimer(newTimer);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsSignupComplete(false); // 모달을 닫을 때 회원가입 완료 상태를 초기화
  };

  const phoneAuth = async () => {
    try {
      const phone = getValues('phone');
      if (phone) {
        const uuid = await requestPhoneAuth(phone, 'driver');
        setPhoneAuthUUID(uuid);
        setModalMessage('인증번호가 전송되었습니다. 번호를 확인해주세요.');
        setIsModalOpen(true);
        startTimer(); // 타이머 시작
      } else {
        setModalMessage('휴대폰 번호를 입력해주세요.');
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('휴대폰 본인 인증 요청 실패:', error);
      setModalMessage('본인 인증 요청에 실패했습니다.');
      setIsModalOpen(true);
    }
  };

  const verifyAuthCode = async (authCode: string) => {
    if (!phoneAuthUUID) {
      setModalMessage('인증 시간이 만료되었습니다. 다시 시도해주세요.');
      setIsModalOpen(true);
      return;
    }

    try {
      // const message = await verifyPhoneAuth(phoneAuthUUID, authCode);
      setModalMessage('본인 인증에 성공하였습니다.');
      setIsModalOpen(true);
      setIsPhoneVerified(true);
      setIsPhoneDisabled(true);

      if (timer) {
        clearTimeout(timer); // 인증 성공 시 타이머 해제
      }
    } catch (error) {
      console.error('인증 실패:', error);
      setModalMessage('본인 인증 요청에 실패하였습니다.');
      setIsModalOpen(true);
    }
  };

  return (
    <Container>
      <Title>
        <h3>기사 회원 정보</h3>
        <span>회원 여부 확인 및 가입을 진행합니다.</span>
      </Title>
      <form onSubmit={handleSubmit(onSubmit)}>
        <JoinInput
          name="passid"
          label="차량번호"
          btn
          placeholder="차량번호 입력 ex.대전 12가 1234"
          errors={
            errors.passid?.message ? [errors.passid.message as string] : []
          }
          onClick={idCheckHandler}
          register={register}
        />
        <JoinInput
          name="password"
          label="비밀번호"
          placeholder="비밀번호 입력"
          type="password"
          errors={
            errors.password?.message ? [errors.password.message as string] : []
          }
          register={register}
        />
        <JoinInput
          name="passwordConfirm"
          label="비밀번호 확인"
          placeholder="비밀번호 입력 확인"
          type="password"
          errors={
            errors.passwordConfirm?.message
              ? [errors.passwordConfirm.message as string]
              : []
          }
          register={register}
        />
        <JoinPhoneInput
          name="phone"
          label="휴대폰 번호"
          placeholder="휴대폰 번호 입력"
          type="text"
          btn
          btnTitle="본인인증"
          disabled={isPhoneDisabled}
          errors={errors.phone?.message ? [errors.phone.message as string] : []}
          register={register}
          validation={{
            onChange: (e: any) => {
              const formattedValue = formatPhoneNumber(e.target.value);
              setValue('phone', formattedValue, { shouldValidate: true });
            },
          }}
          onClick={phoneAuth}
          className={`input-disabled ${isPhoneDisabled ? 'disabled' : ''}`}
        />
        {/* 인증 번호 입력 필드 */}
        {phoneAuthUUID && !isPhoneVerified && (
          <div className="timer-wrapper">
            <JoinInput
              name="authCode"
              label="인증번호"
              placeholder="인증번호 입력"
              type="text"
              btn
              btnTitle="인증확인"
              register={register}
              onClick={() => verifyAuthCode(getValues('authCode') || '')}
            />
            <p>
              남은 시간: {Math.floor(remainingTime / 60)}:
              {remainingTime % 60 < 10
                ? `0${remainingTime % 60}`
                : remainingTime % 60}
            </p>
          </div>
        )}
        <FileUploadImg
          title="자동차 등록증 첨부하기"
          height="150"
          name="registrationFile"
          register={register}
          setValue={setValue}
          errors={
            errors.registrationFile?.message
              ? [errors.registrationFile.message as string]
              : []
          }
        />
        <InputBtn name="회원가입 완료" type="submit" />
      </form>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose} // 회원가입 완료 시에만 로그인 화면으로 이동
        />
      )}
    </Container>
  );
};

export default Join;

//styled
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 2rem 9rem;
  height: 100%;
  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    height: 100%;
    overflow: auto;
    padding-bottom: 3rem;
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera*/
    }
    .select-wrapper {
      padding: 1.1rem 1.1rem 1.6rem 1.5rem;
      background-color: ${(props) => props.theme.gray100};
      border-radius: 0.8rem;
      display: flex;
      flex-direction: column;
      font-size: 1.6rem;
      font-weight: 600;
      span {
        font-size: 1.2rem;
        font-weight: 600;
        color: ${(props) => props.theme.joinLabelColor};
        margin-bottom: 0.6rem;
        line-height: 1.5rem;
      }
      .search-select {
        .react-select__placeholder {
          color: ${(props) => props.theme.placeholderColor};
          font-weight: 400;
        }
        .react-select__control {
          background-color: transparent;
          min-height: 2rem;
          height: 2rem;
          border: none;
          .react-select__value-container {
            padding: 0;
          }
        }
        .react-select__indicator {
          padding: 0;
        }
      }
    }
  }
  .input-disabled {
    position: relative;
    &.disabled {
      opacity: 0.4;
      > div {
        background-color: #f0f0f0;
      }
      &::after {
        content: '';
        top: 0;
        left: 0;
        position: absolute;
        width: 100%;
        height: 100%;
        background-color: black;
        opacity: 0.2;
        border-radius: 0.8rem;
      }
    }
  }
  .timer-wrapper {
    position: relative;
    p {
      position: absolute;
      bottom: 2rem;
      right: 8rem;
      font-size: 1.3rem;
      width: 8.5rem;
    }
  }
`;

export const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  margin-bottom: 2.4rem;
  h3 {
    font-size: 2.4rem;
    font-weight: 700;
  }
  span {
    font-size: 1.4rem;
    font-weight: 400;
  }
`;
